import { t } from "i18next";
import { Download, Play, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { JSX } from "react";
import { BackgroundVisitUploadStatus } from "@/common/enums/backgroundVisitUploadStatus";
import { downloadAudio } from "@/lib/download-audio";
import { bytesToMegabytes, cn } from "@/lib/utils";
import type { BackgroundVisitUpload } from "@/store/backgroundVisitUpload.store";
import { Button } from "../button";
import { CircularProgress } from "../CircularProgress";
import { underV3 } from "../v3/flagged";

interface Props {
  backgroundVisitUpload: BackgroundVisitUpload;
  onCancel: (id: string) => void;
  onStart: (id: string) => void;
  onRestart?: (id: string) => void;
}

export const BackgroundVisitUploadItem = ({
  backgroundVisitUpload,
  onCancel,
  onStart,
  onRestart,
}: Props): JSX.Element => {
  const unsent =
    backgroundVisitUpload.status === BackgroundVisitUploadStatus.FAILED ||
    backgroundVisitUpload.status === BackgroundVisitUploadStatus.CANCELLED;
  return (
    <div
      className={cn(
        "flex items-center justify-between py-4 px-6",
        underV3("gap-3 px-4 py-3 font-v3"),
      )}
    >
      <div className={cn("space-y-1 grow", underV3("min-w-0 space-y-0.5"))}>
        <h3
          className={cn(
            "text-primary text-md font-bold",
            underV3("truncate text-[14px] font-semibold text-v3-text"),
          )}
        >
          {backgroundVisitUpload.id}
        </h3>
        <span className={cn("text-sm", underV3("text-[13px] text-v3-text-3"))}>
          {bytesToMegabytes(backgroundVisitUpload.data.file.size).toFixed(2)} MB
        </span>
        {backgroundVisitUpload.error && (
          <p
            role="alert"
            className={cn("text-sm text-error", underV3("text-v3-rec-text"))}
          >
            {backgroundVisitUpload.error}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {unsent && (
          <Button
            size="sm"
            variant="outline"
            aria-label={t(
              "backgroundVisitUpload.downloadAudio",
              "Download audio",
            )}
            onClick={() => {
              void downloadAudio(backgroundVisitUpload.data.file);
            }}
          >
            <Download />
          </Button>
        )}
        {unsent && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (
                backgroundVisitUpload.kind === "attachment" &&
                backgroundVisitUpload.rejected &&
                onRestart
              )
                onRestart(backgroundVisitUpload.id);
              else onStart(backgroundVisitUpload.id);
            }}
          >
            {backgroundVisitUpload.kind === "attachment" &&
            backgroundVisitUpload.rejected &&
            onRestart
              ? t(
                  "backgroundVisitUpload.startNewAttachment",
                  "Start new attachment",
                )
              : t("backgroundVisitUpload.retrySameFile", "Retry same file")}
          </Button>
        )}
        <AnimatePresence mode="wait">
          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.UPLOADING && (
            <motion.div
              key="uploading"
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2"
              exit={{ opacity: 0, scale: 0.8 }}
              initial={{ opacity: 0, scale: 0.8 }}
            >
              <CircularProgress
                size={40}
                strokeWidth={5}
                value={backgroundVisitUpload.progress}
              />
              <Button
                size={"sm"}
                variant="destructive"
                onClick={() => {
                  onCancel(backgroundVisitUpload.id);
                }}
              >
                <X />
              </Button>
            </motion.div>
          )}

          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.DONE && (
            <motion.span
              key="done"
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                "font-bold text-success",
                underV3("text-[13px] font-semibold text-v3-ok-text"),
              )}
              exit={{ opacity: 0, y: -10 }}
              initial={{ opacity: 0, y: 10 }}
            >
              {t("backgroundVisitUpload.status.done")}
            </motion.span>
          )}

          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.PENDING && (
            <motion.div
              key="pending"
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              initial={{ opacity: 0, scale: 0.8 }}
            >
              <Button
                size={"sm"}
                variant="success"
                onClick={() => {
                  onStart(backgroundVisitUpload.id);
                }}
              >
                <Play />
              </Button>
            </motion.div>
          )}
          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.FAILED && (
            <motion.span
              key="failed"
              animate={{ opacity: 1 }}
              className={cn(
                "font-bold text-error",
                underV3("text-[13px] font-semibold text-v3-rec-text"),
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
            >
              {t("backgroundVisitUpload.status.failed")}
            </motion.span>
          )}
          {backgroundVisitUpload.status ===
            BackgroundVisitUploadStatus.CANCELLED && (
            <motion.span
              key="cancelled"
              animate={{ opacity: 1 }}
              className={cn(
                "font-bold text-error",
                underV3("text-[13px] font-semibold text-v3-rec-text"),
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
            >
              {t("backgroundVisitUpload.status.cancelled")}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
